import React from 'react';
import PropTypes from 'prop-types';
import { reduxForm, FieldArray, change, Form } from 'redux-form';
import { createValidator, passengers } from 'utils/formValidations';
import { setStoredPassenger } from 'utils/storedPassengers';
import taxpayerPayloadFields from 'utils/purchase/taxpayerPayloadFields';
import PassengersStatements from 'components/purchase/passengers/PassengersStatements';
import StaticPassengersForm from 'components/purchase/passengers/StaticPassengersForm';
import usePurchase from 'hooks/store/usePurchase';
import usePassengerAutofill from 'hooks/purchase/passengers/usePassengerAutofill';
import { saveQuickCheckoutPassengers } from '@/actions/quickPurchase';
import { FrequentPassengersProvider } from '../../../context/FrequentPassengers';

// Only validate the passengers array — purchaser billing fields are not rendered
// in this form and are supplied from the purchase state in createPassengerSaveOnSubmit.
const validate = createValidator({
  passengers: [passengers],
});

/**
 * Mirrors the first passenger's data into the purchaser (comprador) form.
 * In QuickCheckout the buyer identity is defined to equal Passenger 1 (the
 * collapsible PurchaserForm hides identity fields), so editing Passenger 1 must
 * keep the buyer in sync — otherwise the user has to edit both (CON-962).
 * Empty values are skipped so passengers that don't collect phone/email don't
 * wipe the buyer phone the pre-fill seeded from storedPurchaser.
 * @param {object} firstPassenger - First passenger's form values.
 * @param {Function} dispatch - Redux dispatch.
 */
const syncPurchaserFromFirstPassenger = (firstPassenger, dispatch) => {
  if (!firstPassenger) return;

  const buyerSync = {
    purchaserFirstName: firstPassenger.firstName,
    purchaserLastName: firstPassenger.lastName,
    purchaserSecondLastName: firstPassenger.secondLastName,
    documentType: firstPassenger.documentType,
    documentId: firstPassenger.documentId,
    email: firstPassenger.email,
    phone: firstPassenger.phone,
    phoneCountry: firstPassenger.phoneCountry,
    phoneCode: firstPassenger.phoneCode,
  };

  Object.entries(buyerSync).forEach(([field, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      dispatch(change('purchaser', field, value));
    }
  });
};

/**
 * onChange handler for the passengers redux-form.
 * Strips non-digit characters from CPF/CNPJ document IDs,
 * and strips non-alphanumeric from RG document IDs.
 * Mirrors PassengersContainer.js:318-351.
 * Also keeps the purchaser (comprador) form in sync with Passenger 1 (CON-962),
 * unless the user has manually edited the buyer (props.buyerManuallyEdited), in
 * which case the manual edit is respected and the sync is skipped.
 * @param {object} values - Current form values.
 * @param {Function} dispatch - Redux dispatch.
 * @param {object} props - Form props (includes buyerManuallyEdited).
 */
const onChange = (values, dispatch, props) => {
  const {
    documentId: purchaserDocumentId,
    documentType: purchaserDocumentType,
    passengers: formPassengers,
  } = values;
  if (
    (purchaserDocumentType === 'CPF' || purchaserDocumentType === 'CNPJ') &&
    purchaserDocumentId
  ) {
    const valueWithOnlyDigits = purchaserDocumentId.replace(/\D/g, '');
    dispatch(change('passengers', 'documentId', valueWithOnlyDigits));
  }
  if (formPassengers) {
    formPassengers.forEach(
      ({ documentId, documentType, documentIdSecond, documentTypeSecond }, index) => {
        if (documentType === 'RG' && documentId) {
          const rgDocumentWithOnlyDigitsAndLetters = documentId.replace(/[^a-zA-Z0-9]/g, '');
          dispatch(
            change(
              'passengers',
              `passengers[${index}].documentId`,
              rgDocumentWithOnlyDigitsAndLetters,
            ),
          );
        }
        if (documentTypeSecond === 'RG' && documentIdSecond) {
          const rgDocumentWithOnlyDigitsAndLetters = documentIdSecond.replace(/[^a-zA-Z0-9]/g, '');
          dispatch(
            change(
              'passengers',
              `passengers[${index}].documentIdSecond`,
              rgDocumentWithOnlyDigitsAndLetters,
            ),
          );
        }
      },
    );
  }

  if (!props?.buyerManuallyEdited) {
    syncPurchaserFromFirstPassenger(formPassengers?.[0], dispatch);
  }
};

/**
 * onSubmitSuccess handler for the passengers redux-form.
 * Persists the passenger array to localStorage and calls the onPassengersSaved callback
 * (used by QuickCheckout to chain payment). Does not overwrite purchaser localStorage
 * because this form only edits passenger data, not the billing contact.
 * @param {*} result - Submission result.
 * @param {Function} dispatch - Redux dispatch.
 * @param {object} props - Form props with values and onPassengersSaved.
 */
const onSubmitSuccess = (result, dispatch, props) => {
  setStoredPassenger(props.values.passengers);
  props.onPassengersSaved?.();
};

/**
 * onSubmitFail handler for the passengers redux-form.
 * Calls the optional onPassengersSubmitFail callback to reject the payment chain promise.
 * @param {object} errors - Validation errors.
 * @param {Function} dispatch - Redux dispatch.
 * @param {*} submitError - Submit error.
 * @param {object} props - Form props with onPassengersSubmitFail.
 */
const onSubmitFail = (errors, dispatch, submitError, props) => {
  props.onPassengersSubmitFail?.();
};

/**
 * QuickCheckoutPassengersFormBase component.
 * Functional, hooks-based passenger form for QuickCheckout.
 * Wraps StaticPassengersForm via the redux-form 'passengers' form. Quick Checkout is
 * seat-selection only (no open-ticket support), so the dynamic (no-seat) form is not used.
 * No own submit button — submit is driven externally by QuickCheckout's Pay button chain.
 * @param {object} props - handleSubmit, onSubmit.
 * @param {Function} props.handleSubmit - Redux-form handleSubmit.
 * @param {Function} props.onSubmit - External submit handler.
 */
const QuickCheckoutPassengersFormBase = ({ handleSubmit, onSubmit }) => {
  const purchase = usePurchase();

  const { departs, returns, roundTrip, insuranceOutgoingUnitAmount, insuranceIncomingUnitAmount } =
    purchase;

  const { autofillEnabled, toggleAutofill } = usePassengerAutofill();

  const isDepartureOpen = departs ? Boolean(departs.openTicket) : false;
  const isReturnOpen = returns ? Boolean(returns.openTicket) : false;
  const isOpenTicket = isDepartureOpen && (!roundTrip || isReturnOpen);

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FrequentPassengersProvider>
        <FieldArray
          name="passengers"
          component={StaticPassengersForm}
          hideHeader
          // Quick Checkout is adults-only (search eligibility rejects child/minor/infant), so the
          // passenger category selector must not render. An empty list makes PassengerBusCategories
          // short-circuit; each passenger keeps its busCategory from initialValues.
          busCategories={[]}
          updatingCategories={purchase.isFetchingDetails || false}
          autofillEnabled={autofillEnabled}
          toggleAutofill={toggleAutofill}
          deletePassenger={() => {}}
          isOpenTicket={isOpenTicket}
          insuranceOutgoingUnitAmount={insuranceOutgoingUnitAmount}
          insuranceIncomingUnitAmount={insuranceIncomingUnitAmount}
          isExchange={purchase.isExchange || false}
          isRoundTrip={roundTrip}
          user={null}
          showPetFriendlySeatsWarn={() => {}}
          loyaltyHolderName={null}
          isLogged={false}
          initialValues={purchase.passengers || []}
          isAccountThePassenger={false}
          autofillFirstPassenger={() => {}}
          overbooking={false}
        />
      </FrequentPassengersProvider>

      <div className="mt-4">
        <PassengersStatements />
      </div>
    </Form>
  );
};

QuickCheckoutPassengersFormBase.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

/**
 * QuickCheckoutPassengersForm component.
 * Redux-form HOC wrapper for QuickCheckoutPassengersFormBase.
 * initialValues are passed as a prop and rehydrated via enableReinitialize.
 */
const QuickCheckoutPassengersForm = reduxForm({
  form: 'passengers',
  // Keep the form state alive across the inline seat editor's unmount and preserve the user's
  // dirty identity edits across the store reverts that follow a seat re-lock (the seat relock's
  // getPurchase plus later state-driven refreshes all mergeDeep backend passengers over the store,
  // changing the store-derived initialValues). Seats are not registered Fields, so they are not
  // refreshed by keepDirtyOnReinitialize — QuickCheckout syncs passengers[i].seats via change()
  // when the inline seat editor closes (PRO2-8).
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  shouldValidate: () => true,
  onChange,
  onSubmitSuccess,
  onSubmitFail,
  validate,
})(QuickCheckoutPassengersFormBase);

export default QuickCheckoutPassengersForm;

/**
 * buildTripsForPassengerSave.
 * Build the trips object expected by saveQuickCheckoutPassengers from the current purchase state.
 * Reuses already-selected seats — does NOT re-select.
 * @param {object} purchase - Normalized purchase state from usePurchase.
 * @returns {object} trips - { departs: [...], returns?: [...] }
 */
function buildTripsForPassengerSave(purchase) {
  const { departs, returns, roundTrip } = purchase;

  const trips = {
    departs: (departs.fragments || []).map(({ id, transportType }) => ({
      id,
      type: transportType || 'bus',
      selectedSeats: departs.selectedSeats || [],
    })),
  };

  if (roundTrip && returns?.fragments) {
    trips.returns = returns.fragments.map(({ id, transportType }) => ({
      id,
      type: transportType || 'bus',
      selectedSeats: returns.selectedSeats || [],
    }));
  }

  return trips;
}

/**
 * createPassengerSaveOnSubmit.
 * Create the onSubmit handler for QuickCheckoutPassengersForm.
 * Dispatches saveQuickCheckoutPassengers with the current purchase token and built trips.
 * Purchaser billing fields (name, email, phone, document) are supplied from the purchase
 * state rather than the form values — the QuickCheckout passengers form only edits
 * passenger data (names, document IDs, categories), not the billing contact.
 * @param {Function} dispatch - Redux dispatch.
 * @param {object} purchase - Normalized purchase state.
 * @param {object} [options] - Extra options.
 * @param {boolean} [options.buyerManuallyEdited] - Whether the user edited the buyer by hand.
 * @param {object} [options.purchaserFormValues] - Current purchaser form values (buyer source when edited).
 * @returns {Function} onSubmit(fields) handler.
 */
export function createPassengerSaveOnSubmit(dispatch, purchase, options = {}) {
  const { buyerManuallyEdited = false, purchaserFormValues = {} } = options;

  return (fields) => {
    const trips = buildTripsForPassengerSave(purchase);
    // Prefer the just-edited first passenger from the submitted form so buyer data linked to
    // Passenger 1 follows edits (CON-962). This path skips onSetPurchaser, so purchase.* stays
    // stale; the edited fields are the source of truth. Fall back to purchase state, then form
    // purchaser fields so GFA backends that validate names don't crash with empty strings.
    const firstPassenger = fields.passengers?.[0] || purchase.passengers?.[0] || {};

    // Phone, country and calling code are resolved together from ONE source, in both branches:
    // pairing a number from the buyer form with a calling code from the purchase would build a
    // malformed number. Only the priority differs — a manual buyer edit wins over Passenger 1, and
    // otherwise Passenger 1 wins (CON-962) for tenants that collect a passenger phone. The rest
    // render no passenger phone field, so the buyer form is the only place the number exists;
    // reading it here is what keeps the phone the user sees from being dropped from the payload.
    const phoneSources = {
      firstPassenger: {
        phone: firstPassenger.phone,
        country: firstPassenger.phoneCountry,
        code: firstPassenger.phoneCode,
      },
      buyerForm: {
        phone: purchaserFormValues.phone,
        country: purchaserFormValues.phoneCountry,
        code: purchaserFormValues.phoneCode,
      },
      purchase: { phone: purchase.phone, country: purchase.phoneCountry, code: purchase.phoneCode },
      submitted: { phone: fields.phone, country: fields.phoneCountry, code: fields.phoneCode },
    };
    const buyerPhoneSource =
      (buyerManuallyEdited
        ? [phoneSources.buyerForm, phoneSources.purchase, phoneSources.submitted]
        : [
            phoneSources.firstPassenger,
            phoneSources.buyerForm,
            phoneSources.purchase,
            phoneSources.submitted,
          ]
      ).find(({ phone }) => Boolean(phone)) || {};

    // Once the user manually edits the buyer, that edit wins over Passenger 1: build the
    // purchaser fields from the live purchaser form values instead of the first passenger.
    const purchaserFields = buyerManuallyEdited
      ? {
          purchaserFirstName:
            purchaserFormValues.purchaserFirstName ||
            purchase.purchaserFirstName ||
            fields.purchaserFirstName,
          purchaserLastName:
            purchaserFormValues.purchaserLastName ||
            purchase.purchaserLastName ||
            fields.purchaserLastName,
          purchaserSecondLastName:
            purchaserFormValues.purchaserSecondLastName ||
            purchase.purchaserSecondLastName ||
            fields.purchaserSecondLastName,
          email: purchaserFormValues.email || purchase.email || fields.email,
          phone: buyerPhoneSource.phone,
          phoneCountry: buyerPhoneSource.country,
          phoneCode: buyerPhoneSource.code,
          documentType:
            purchaserFormValues.documentType || purchase.documentType || fields.documentType,
          documentId: purchaserFormValues.documentId || purchase.documentId || fields.documentId,
        }
      : {
          purchaserFirstName:
            firstPassenger.firstName || purchase.purchaserFirstName || fields.purchaserFirstName,
          purchaserLastName:
            firstPassenger.lastName || purchase.purchaserLastName || fields.purchaserLastName,
          purchaserSecondLastName:
            firstPassenger.secondLastName ||
            purchase.purchaserSecondLastName ||
            fields.purchaserSecondLastName,
          email: firstPassenger.email || purchase.email || fields.email,
          phone: buyerPhoneSource.phone,
          phoneCountry: buyerPhoneSource.country,
          phoneCode: buyerPhoneSource.code,
          documentType: firstPassenger.documentType || purchase.documentType || fields.documentType,
          documentId: firstPassenger.documentId || purchase.documentId || fields.documentId,
        };

    // PRO2-128: the invoice the buyer entered belongs to the buyer, not to Passenger 1, so it is
    // taken from the live purchaser form in both branches. Without it this path — which builds its
    // own payload and never reaches `updatePurchaseFields` — dropped the RUC whenever a passenger
    // was edited, and the provider issued a boleta instead of a factura.
    const mergedFields = {
      ...fields,
      ...purchaserFields,
      ...taxpayerPayloadFields(purchaserFormValues),
    };
    return dispatch(
      saveQuickCheckoutPassengers(purchase.token, mergedFields, trips, {
        retrySmartSelection: true,
      }),
    );
  };
}
